import Banner from '@components/atomic/atoms/Banner/Banner';
import { RejectModal, SuccessModal } from '@components/atomic/organisms';
import UploadButton from '@components/form/upload/UploadButton';
import { IcoUploadDocument } from '@icons';
import { importCompiledTemplate } from '@lib/api_ts/salTemplatesApi';
import { useMutation } from '@tanstack/react-query';
import React, { Dispatch, SetStateAction } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { useParams } from 'react-router-dom';

interface MassiveSalUploadModalProps {
	isModalOpen: boolean;
	setIsModalOpen: Dispatch<SetStateAction<boolean>>;
}

const MassiveSalUploadModal: React.FC<MassiveSalUploadModalProps> = ({ isModalOpen, setIsModalOpen }) => {
	const { projectId } = useParams<{ projectId: string }>();

	const invoicesUploadMutation = useMutation({
		mutationFn: (data: { file: File }) => {
			return importCompiledTemplate(projectId!, 'invoices', data.file);
		},
	});

	const timesheetsUploadMutation = useMutation({
		mutationFn: (data: { file: File }) => {
			return importCompiledTemplate(projectId!, 'timesheets', data.file);
		},
	});

	const itemsToUpload = [
		{
			name: 'invoices',
			key: 'invoices',
			label: 'Rendiconto costi esterni',
			description: 'colonne obbligatorie: CUP/ID PROGETTO · ID FATTURA · POSIZIONE · CENTRO DI COSTO · ID QUOTA · IMPORTO QUOTA',
			mutation: invoicesUploadMutation,
			accepts: '.xlsx, .xls',
			maxSize: 50 * 1024 * 1024, // 50 MB in byte
		},
		{
			name: 'timesheets',
			key: 'timesheets',
			label: 'Rendiconto costi interni',
			description:
				'colonne obbligatorie: CUP/ID PROGETTO · WP/STREAM · NR. RECORD · ANNO · MESE · MATRICOLA · CATEGORIA · COSTO SUL PROGETTO',
			mutation: timesheetsUploadMutation,
			accepts: '.xlsx, .xls',
			maxSize: 50 * 1024 * 1024, // 50 MB in byte
		},
	] as const;

	const [isTooBig, setIsTooBig] = React.useState(false);

	return (
		<>
			<Modal centered size="xl" show={isModalOpen} className="custom-modal-xl" onHide={() => setIsModalOpen(false)}>
				<Modal.Header
					style={{
						flexDirection: 'column',
						justifyContent: 'center',
						alignItems: 'center',
						gap: '1rem',
						padding: '1.5rem',
					}}
				>
					<IcoUploadDocument
						style={{
							fill: 'var(--np-primary-color)',
						}}
					/>
					<Modal.Title>
						<h5
							style={{
								fontWeight: 700,
								fontSize: '24px',
								textAlign: 'center',
								marginBottom: 0,
							}}
						>
							Caricamento massivo bozza rendiconto
						</h5>
					</Modal.Title>

					<div>
						<p
							style={{
								marginBottom: 0,
								textAlign: 'center',
							}}
						>
							Carica uno o entrambi i template compilati. Al termine riceverai una notifica in-app con il log dettagliato dell'importazione
						</p>
					</div>
				</Modal.Header>
				<Modal.Body
					style={{
						padding: '1.5rem',
					}}
				>
					<div
						style={{
							display: 'grid',
							gap: '1rem',
						}}
					>
						{itemsToUpload.map((item) => (
							<div
								key={item.name}
								style={{
									padding: '1rem',
									border: '1px solid #2A3A64',
									borderRadius: '8px',
									display: 'grid',
									gridTemplateColumns: '1fr auto',
									gap: '3rem',
									alignItems: 'center',
								}}
							>
								<div>
									<span
										style={{
											fontWeight: 700,
											display: 'block',
										}}
									>
										{item.label}
									</span>
									<span
										style={{
											display: 'block',
										}}
									>
										{item.description}
									</span>
									{item.name === 'timesheets' ? (
										<>
											<hr />
											<span>
												La colonna <b>GIORNO</b> diventa obbligatoria in presenza di piu righe con lo stesso NR. RECORD, ANNO e MESE.
											</span>
										</>
									) : null}
								</div>
								<div>
									<UploadButton itemToUpload={item} setIsTooBig={setIsTooBig} />
								</div>
								<SuccessModal
									show={item.mutation.isSuccess}
									onHide={item.mutation.reset}
									message="L'elaborazione dei tuoi file è iniziata. Riceverai una notifica in-app con il link al file del log completo scaricabile."
								/>
								<RejectModal
									show={item.mutation.isError}
									onHide={item.mutation.reset}
									message={
										item.mutation.error?.message.includes("'file'")
											? 'Il file caricato è in un formato non idoneo. Sono accettati solo file .xlsx conformi al template scaricato dalla piattaforma.'
											: item.mutation.error?.message || "Si è verificato un errore durante l'elaborazione del file. Riprova più tardi."
									}
								/>
							</div>
						))}
						<Banner
							variant="warning"
							message={
								<p style={{ marginBottom: 0 }}>
									<b>NB</b>: se si hanno a disposizione anche i dati considerati non obbligatori è bene inserirli nel template per
									completezza informativa.
								</p>
							}
						/>
					</div>
				</Modal.Body>
				<Modal.Footer>
					<Button
						className="btn custom-outline-button"
						style={{
							height: '48px',
						}}
						onClick={() => {
							setIsModalOpen(false);
						}}
					>
						Annulla
					</Button>
				</Modal.Footer>
			</Modal>
			<RejectModal
				show={isTooBig}
				onHide={() => {
					setIsTooBig(false);
				}}
				message="Il file selezionato supera i 50 MB. Seleziona un file più piccolo."
			/>
		</>
	);
};

export default MassiveSalUploadModal;
